import {
  useApiMutation,
  useApiQuery,
  queryClient,
} from "../services/react-query";
import { callApi } from "../services/api";
import { AUTH_OPT_IN_HEADERS } from "../services/authSession";

export const useGetWorkspaces = (params) =>
  useApiQuery({
    queryOptions: { queryKey: ["workspaces", params] },
    fetchOptions: { url: `/workspaces`, method: "GET", params },
  });

export const useGetWorkspace = (id) =>
  useApiQuery({
    queryOptions: { queryKey: ["workspace", { id }], enabled: !!id },
    fetchOptions: { url: `/workspaces/${id}`, method: "GET" },
  });

export const usePatchWorkspace = ({ id, config }) =>
  useApiMutation({
    mutationOptions: { mutationKey: "usePatchWorkspace" },
    fetchOptions: { url: `/workspaces/${id}`, method: "PATCH" },
    invalidateQueryKeys: [["workspaces"]],
    successMessage: { title: "Workspace has been updated", color: "green" },
    ...config,
  });

export const useCreateWorkspace = () =>
  useApiMutation({
    mutationOptions: { mutationKey: "useCreateWorkspace" },
    fetchOptions: { url: "/workspaces", method: "POST" },
    invalidateQueryKeys: [["workspaces"]],
    successMessage: { title: "Workspace has been created", color: "green" },
  });

export const useGetWorkspaceUsers = (id) =>
  useApiQuery({
    queryOptions: { queryKey: ["workspaceUsers", { id }], enabled: !!id },
    fetchOptions: { url: `/workspaces/${id}/users`, method: "GET" },
  });

export const useRemoveUserFromWorkspace = ({ workspaceId }) =>
  useApiMutation({
    mutationOptions: { mutationKey: "useRemoveUserFromWorkspace" },
    fetchOptions: {
      url: `/workspaces/${workspaceId}/users`,
      method: "DELETE",
    },
    invalidateQueryKeys: [["workspaceUsers"]],
    successMessage: {
      title: "User has been removed from workspace",
      color: "green",
    },
  });

export const useTransferWorkspaceOwnership = ({ workspaceId }) =>
  useApiMutation({
    mutationOptions: { mutationKey: "useTransferWorkspaceOwnership" },
    fetchOptions: { url: `/workspaces/${workspaceId}`, method: "PATCH" },
    invalidateQueryKeys: [["workspace"]],
    successMessage: {
      title: "Workspace ownership transferred",
      color: "green",
    },
  });

export const useDeleteWorkspace = () =>
  useApiMutation({
    mutationOptions: { mutationKey: "useDeleteWorkspace" },
    fetchOptions: { url: `/workspaces`, method: "DELETE" },
    invalidateQueryKeys: [["workspaces"]],
    successMessage: { title: "Workspace has been deleted", color: "green" },
  });

// --- Workspace invitations (owner/sysAdmin) ---

export const useGetWorkspaceInvitations = (id) =>
  useApiQuery({
    queryOptions: {
      queryKey: ["workspaceInvitations", { id }],
      enabled: !!id,
    },
    fetchOptions: { url: `/workspaces/${id}/invitations`, method: "GET" },
  });

// An email with a Congrs account is added to the workspace on the spot
// (`outcome: "added"`); one without gets an emailed invitation
// (`outcome: "invited"`). Both lists can change, so both are refreshed.
// With `sendEmail: false` (sysAdmin only) nothing is emailed: the account is
// added silently, or the invitation is recorded as "not sent".
export const useInviteToWorkspace = ({ workspaceId }) =>
  useApiMutation({
    mutationOptions: { mutationKey: "useInviteToWorkspace" },
    fetchOptions: {
      url: `/workspaces/${workspaceId}/invitations`,
      method: "POST",
    },
    invalidateQueryKeys: [["workspaceInvitations"], ["workspaceUsers"]],
    successMessage: (data, variables) => {
      const skippedEmail = variables?.sendEmail === false;

      if (data?.outcome !== "added") {
        return skippedEmail
          ? {
              title: "Invitation recorded",
              message: "No email was sent. Use “Send email” when ready.",
              color: "green",
            }
          : { title: "Invitation sent", color: "green" };
      }

      if (skippedEmail) {
        return {
          title: "User added to workspace",
          message: "No email was sent.",
          color: "green",
        };
      }

      // The membership stands even when the notification could not go out;
      // say so, since the person will not hear about it any other way.
      return data.emailSent === false
        ? {
            title: "User added to workspace",
            message:
              "The notification email could not be sent. Let them know they can log in to Congrs.",
            color: "yellow",
          }
        : { title: "User added to workspace", color: "green" };
    },
  });

// Sends the accept link for an invitation recorded without an email, or a
// new link for an expired one. The 7-day expiry starts now.
export const useSendWorkspaceInvitation = ({ workspaceId, invitationId }) =>
  useApiMutation({
    mutationOptions: {
      mutationKey: ["useSendWorkspaceInvitation", invitationId],
    },
    fetchOptions: {
      url: `/workspaces/${workspaceId}/invitations/${invitationId}/send`,
      method: "POST",
    },
    invalidateQueryKeys: [["workspaceInvitations"]],
    successMessage: { title: "Invitation sent", color: "green" },
  });

// Who is behind an email, so the invite form can show the match and ask
// before adding. Fetched on submit rather than as the user types: the answer
// only matters at the moment of the decision. Owner-only, like inviting.
export const lookupWorkspaceAccount = ({ workspaceId, email }) =>
  queryClient.fetchQuery({
    queryKey: ["workspaceAccountLookup", { workspaceId, email }],
    queryFn: () =>
      callApi({
        url: `/workspaces/${workspaceId}/invitations/lookup?email=${encodeURIComponent(email)}`,
        method: "GET",
        token: localStorage.getItem("token"),
      }),
  });

export const useRevokeWorkspaceInvitation = ({ workspaceId, invitationId }) =>
  useApiMutation({
    mutationOptions: {
      mutationKey: ["useRevokeWorkspaceInvitation", invitationId],
    },
    fetchOptions: {
      url: `/workspaces/${workspaceId}/invitations/${invitationId}`,
      method: "DELETE",
    },
    invalidateQueryKeys: [["workspaceInvitations"]],
    successMessage: { title: "Invitation revoked", color: "green" },
  });

// --- Public invitation accept (no auth) ---

export const useGetWorkspaceInvite = (token) =>
  useApiQuery({
    queryOptions: {
      queryKey: ["workspaceInvite", { token }],
      enabled: !!token,
      retry: 0,
    },
    fetchOptions: { url: `/workspace-invitations/${token}`, method: "GET" },
  });

export const useAcceptWorkspaceInvite = (token) =>
  useApiMutation({
    mutationOptions: { mutationKey: ["useAcceptWorkspaceInvite", token] },
    fetchOptions: {
      url: `/workspace-invitations/${token}/accept`,
      method: "POST",
      // A new account comes back signed in.
      headers: AUTH_OPT_IN_HEADERS,
    },
    invalidateQueryKeys: [
      ["workspaceInvite", { token }],
      ["workspaces"],
      ["workspaceUsers"],
    ],
    hideNotification: true,
  });
